import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { ReactNode } from "react";

import { redirectToLogin } from "@/api/client";
import { parseCallStatus } from "@/telephony/state";
import { useSoftphoneStore } from "@/telephony/store";
import type { ServerEvent } from "./events";
import { RealtimeSocket, presenceUrl } from "./socket";
import { useRealtimeStore } from "./store";

/**
 * Which cached queries an event makes stale.
 *
 * The mapping is here and not in the socket on purpose: the socket's job is to
 * stay connected, and what a `whatsapp.message` invalidates changes as screens
 * are added. The inbox (.docs/04 §7) extends this table; it does not touch
 * `socket.ts`.
 */
function invalidationsFor(event: ServerEvent): unknown[][] {
  switch (event.type) {
    case "telephony.call":
      // D9: a finished call writes an `Activity`, so every open timeline is now
      // one row short.
      return [["activities"], ["call", event.call], ["calls"]];
    case "whatsapp.message":
      return [
        ["activities"],
        ["conversations"],
        ["messages", event.conversation],
        ["conversation", event.conversation],
      ];
    case "conversation.updated":
      return [["conversations"], ["conversation", event.conversation]];
    case "mail.message":
      // D33: a sent email writes an `Activity`, so an open timeline is a row
      // short — the same reason a finished call invalidates them.
      return [["mail"], ["activities"]];
    case "mail.read":
    case "mail.mailbox":
      return [["mail"]];
    default:
      return [];
  }
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const setStatus = useRealtimeStore((state) => state.setStatus);

  useEffect(() => {
    const socket = new RealtimeSocket({
      url: presenceUrl(),
      onStatusChange: setStatus,
      onEvent: (event) => {
        // The call bar is not a query. It is driven straight off the socket so
        // that "timbrando" changes to "en curso" the instant Twilio says so,
        // rather than one refetch later — during a call, a second of lag is
        // the agent talking over a ringing line (.docs/04 §6).
        if (event.type === "telephony.call") {
          const status = parseCallStatus(event.status);
          if (status) {
            useSoftphoneStore
              .getState()
              .applyServer(event.call, status, event.duration);
          }
        }
        for (const queryKey of invalidationsFor(event)) {
          void queryClient.invalidateQueries({ queryKey });
        }
      },
      // A socket that was down for thirty seconds means everything on screen
      // may be stale, and the user has no way to know that. Refetch the lot.
      // Only on a *re*connect: the first open follows the initial fetch, and
      // invalidating there would double every request at boot.
      onReconnect: () => void queryClient.invalidateQueries(),
      // The session expired while the tab sat open. The REST client would
      // discover this on the next request; the socket found out first.
      onUnauthenticated: redirectToLogin,
    });

    socket.start();

    // Waiting out a backoff after the user has physically come back to the
    // machine is the difference between instant and broken.
    const wake = () => {
      if (document.visibilityState === "visible") socket.reconnectNow();
    };
    window.addEventListener("online", wake);
    document.addEventListener("visibilitychange", wake);

    return () => {
      window.removeEventListener("online", wake);
      document.removeEventListener("visibilitychange", wake);
      socket.stop();
    };
  }, [queryClient, setStatus]);

  return <>{children}</>;
}
